import i18next from 'i18next';
import {
    initReactI18next,
    useTranslation as i18nUseTranslation,
} from 'react-i18next';
import Backend from './Backend';

const LOCAL_STORAGE_KEY = 'language';

/**
 * Get translated string for the given key and options
 *
 * @param {string} key translation key (for example, 'common.search')
 * @param {...any} rest options for i18next's 't' function
 * @see https://www.i18next.com/overview/api#t
 * @returns {string} translated string
 */
export function getTranslation(key, ...rest) {
    return i18next.t(key, ...rest);
}

/**
 * Get array of tranlated strings for given array of keys with or without options
 *
 * @param {...(string|Array)} args translation keys or array containing key and options, e.g., ['key', options]
 * @returns {string[]} array of translated strings
 */
export function getTranslations(...args) {
    return args.map((arg) =>
        Array.isArray(arg) ? getTranslation(...arg) : getTranslation(arg),
    );
}

/**
 * useTranslation hook for functional components
 *
 * @returns {Object} { getTranslation } object
 */
export function useTranslation() {
    const { t, ready, ...rest } = i18nUseTranslation();

    return {
        getTranslation: t,
        isI18nReady: ready,
        ...rest,
    };
}

/**
 * Get currently selected language or default language
 *
 * @returns {string} language (for example, 'en-US')
 */
export function getLanguage() {
    return window.localStorage.getItem(LOCAL_STORAGE_KEY) || i18next.language;
}

/**
 * Set new language
 *
 * @param {string} newLanguage language (for example, 'en-US')
 * @param {Object} options
 * @param {options.language} [newLanguage] language to update translations for
 * @param {options.strings} [options={}] object containing strings values
 */
export function setLanguage(language = 'en-US') {
    if (getLanguage() !== language) {
        window.localStorage.setItem(LOCAL_STORAGE_KEY, language);

        return i18next.changeLanguage(language);
    }

    return Promise.resolve();
}

/**
 * Initialize translation
 *
 * @param {Object} options Options
 * @param {string} [options.fallbackLng='en-US'] Fallback language. Used when the app could not load default or previously selected language.
 * @param {Object} [options.defaultTranslations] Object containing fallback strings for all languages in the form of {<locale>: Promise returning strings object}.
 * @param {string} [options.loadPath] Path to API endpoint returning translation strings.
 * @param {string} [options.localizationPath] path prefix for loading localization files. local specific files will be loaded from this
 * @param {boolean} [options.shouldLoadStrings] Defines if initTranslations should make an attempt to load strings from loadPath location.
 */
export function initTranslations({
    fallbackLng = 'en-US',
    defaultTranslations,
    loadPath,
    shouldLoadStrings = true,
    localizationPath,
} = {}) {
    const isTestEnv = process.env.NODE_ENV === 'test';

    const shouldLoadStringsOverride = shouldLoadStrings && !isTestEnv;

    return i18next
        .use(initReactI18next)
        .use(Backend)
        .init({
            fallbackLng,
            interpolation: {
                escapeValue: false,
            },
            lng: getLanguage(),
            load: 'currentOnly',
            ns: ['translation'],
            partialBundledLanguages: true,
            backend: {
                loadURL: loadPath,
                defaultTranslations,
                shouldLoadStrings: shouldLoadStringsOverride,
                localizationPath,
            },
            react: {
                useSuspense: false,
            },
        });
}
